import { parseGoogleMapsCoords } from './googleMapsCoords'
import { venueNameKey } from './venueNameKey'

/**
 * Custom-venue history on the desk (spec-desk-venue-location, CONTEXT
 * D-01..D-05).
 *
 * The SQL function custom_venue_history (0038) already limited rows to the
 * candidate's town and grouped them by `lower(btrim(venue_name))`. This module
 * merges spellings with venueNameKey (the one stopword list, D-02), picks the
 * pin (D-03 step 3), measures the spread (D-04) and decides the Save-as-venue
 * offer (D-05):
 *
 * - D-01: rows are "the same venue" when their venueNameKey equals the
 *   candidate's; the town match happened in SQL, so a name alone never matches
 *   across towns. An empty key matches nothing.
 * - D-03: google_maps coordinates first, then the most common history pin (the
 *   most used 500 m place, then its most used pin; the most recent breaks a
 *   tie), then nothing.
 * - D-04: pins more than HISTORY_SPREAD_METRES apart (haversine) are counted as
 *   different places, so the desk can say "Earlier events put this venue in N
 *   different places."
 * - D-05: the offer needs SAVE_AS_VENUE_MIN_EARLIER_USES earlier uses and a
 *   name that is not junk (block list, or under 3 characters).
 *
 * Pure: no Supabase, no React, no i18n.
 */

/** Earlier custom-venue events need this many uses (the candidate is the next one, D-05). */
export const SAVE_AS_VENUE_MIN_EARLIER_USES = 2
/** A shorter venue name is never offered as a venue (D-05). */
export const SAVE_AS_VENUE_MIN_NAME_LENGTH = 3
/** Names that are not a place (D-05), compared through venueNameKey. */
export const SAVE_AS_VENUE_BLOCKLIST = ['vários locais', 'online', 'a definir', 'tbd', 'various']
/** History pins further apart than this are different places (D-04). */
export const HISTORY_SPREAD_METRES = 500

/** One distinct (rounded) coordinate pair of a venue's earlier events. */
export type VenueHistoryPair = { lat: number; lng: number; uses: number; lastUsedAt: string | null }

/** One row of custom_venue_history, in the browser's names. */
export type VenueHistoryRow = {
  venueName: string
  useCount: number
  lastUsedAt: string | null
  pairs: VenueHistoryPair[]
  address: string | null
  description: string | null
  descriptionPt: string | null
  phone: string | null
  website: string | null
}

/** The merged rows of one venue (every spelling with the candidate's name key). */
export type VenueHistoryMatch = VenueHistoryRow

export type DeskLocationPick = {
  /** The form's 'lat,lng', or null when there is no pin. */
  location: string | null
  source: 'google_maps' | 'history' | null
  /** Distinct places among the history pins when source is 'history' (D-04), else 0. */
  places: number
}

/** The Save-as-venue form's prefill (D-06). Field names are shared with 33.3-03. */
export type VenueSuggestion = {
  name: string
  address: string
  description: string
  descriptionPt: string
  phone: string
  website: string
  location: string
}

type LatLng = { lat: number; lng: number }

const timeOf = (iso: string | null): number => {
  if (!iso) return -Infinity
  const time = Date.parse(iso)
  return Number.isNaN(time) ? -Infinity : time
}

const laterOf = (a: string | null, b: string | null): string | null => (timeOf(b) > timeOf(a) ? b : a)

const byRecency = <T extends { lastUsedAt: string | null }>(a: T, b: T): number =>
  timeOf(b.lastUsedAt) - timeOf(a.lastUsedAt) || 0

const firstText = (values: (string | null)[]): string | null =>
  values.find((value) => !!value && value.trim().length > 0) ?? null

const mergePairs = (pairs: VenueHistoryPair[]): VenueHistoryPair[] => {
  const merged: VenueHistoryPair[] = []
  for (const pair of pairs) {
    const same = merged.find((seen) => seen.lat === pair.lat && seen.lng === pair.lng)
    if (same) {
      same.uses += pair.uses
      same.lastUsedAt = laterOf(same.lastUsedAt, pair.lastUsedAt)
    } else {
      merged.push({ ...pair })
    }
  }
  return merged
}

/**
 * The rows whose venueNameKey equals the candidate's, merged into one (D-01,
 * D-02): uses summed, identical pairs merged, the name and texts from the most
 * recent row that has them. No key, a junk name or no matching row -> null.
 *
 * A junk name ("Vários locais", "Online", under 3 characters) names no single
 * place, so its history gives no pin, no note and no offer (UAT 2026-11-02:
 * "Vários locais" opened with a pin from earlier events).
 */
export const mergeVenueHistory = (
  rows: VenueHistoryRow[],
  venueName: string | null | undefined
): VenueHistoryMatch | null => {
  const key = venueNameKey(venueName)
  if (!key || isJunkVenueName(venueName)) return null
  const kept = rows.filter((row) => venueNameKey(row.venueName) === key).sort(byRecency)
  if (kept.length === 0) return null
  return {
    venueName: kept[0].venueName,
    useCount: kept.reduce((sum, row) => sum + row.useCount, 0),
    lastUsedAt: kept.reduce<string | null>((latest, row) => laterOf(latest, row.lastUsedAt), null),
    pairs: mergePairs(kept.flatMap((row) => row.pairs)),
    address: firstText(kept.map((row) => row.address)),
    description: firstText(kept.map((row) => row.description)),
    descriptionPt: firstText(kept.map((row) => row.descriptionPt)),
    phone: firstText(kept.map((row) => row.phone)),
    website: firstText(kept.map((row) => row.website))
  }
}

const byUsesThenRecency = (a: VenueHistoryPair, b: VenueHistoryPair): number =>
  b.uses - a.uses || byRecency(a, b)

/** Great-circle distance in metres (haversine; copied from web's haversineKm, not imported). */
export const haversineMetres = (a: LatLng, b: LatLng): number => {
  const R = 6371000 // mean Earth radius, m
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(h))
}

/**
 * How many different places the pins describe (D-04): 0 without pins, 1 when
 * every two are within HISTORY_SPREAD_METRES, else the greedy cluster count
 * (seeds by uses), never less than 2, so any two pins further apart show the
 * note.
 */
export const countHistoryPlaces = (pairs: VenueHistoryPair[]): number => {
  if (pairs.length === 0) return 0
  let widest = 0
  for (let i = 0; i < pairs.length; i++) {
    for (let j = i + 1; j < pairs.length; j++) {
      widest = Math.max(widest, haversineMetres(pairs[i], pairs[j]))
    }
  }
  if (widest <= HISTORY_SPREAD_METRES) return 1
  return Math.max(2, clusterPairs(pairs).length)
}

type PairCluster = { seed: VenueHistoryPair; members: VenueHistoryPair[]; uses: number; lastUsedAt: string | null }

/**
 * Greedy places of HISTORY_SPREAD_METRES: pairs taken by uses, then recency;
 * a pair joins the first seed within reach, else it seeds a new place. So a
 * place's seed is its most-used pair (the most recent on a tie).
 */
const clusterPairs = (pairs: VenueHistoryPair[]): PairCluster[] => {
  const clusters: PairCluster[] = []
  for (const pair of [...pairs].sort(byUsesThenRecency)) {
    const home = clusters.find((cluster) => haversineMetres(cluster.seed, pair) <= HISTORY_SPREAD_METRES)
    if (home) {
      home.members.push(pair)
      home.uses += pair.uses
      home.lastUsedAt = laterOf(home.lastUsedAt, pair.lastUsedAt)
    } else {
      clusters.push({ seed: pair, members: [pair], uses: pair.uses, lastUsedAt: pair.lastUsedAt })
    }
  }
  return clusters
}

/**
 * The pin (D-03 step 3, review WR-04): the place (500 m cluster) with the most
 * uses in total, the most recently used place on a tie; within it, its
 * most-used pair, the most recent on a tie. Pins are compared at 5 decimals
 * (about 1 m), so hand-placed pins of one venue rarely coincide: counting
 * exact pairs alone would let two copies of one wrong pin beat three correct
 * pins a few metres apart. None -> null.
 */
export const pickHistoryPair = (pairs: VenueHistoryPair[]): VenueHistoryPair | null => {
  if (pairs.length === 0) return null
  const [best] = clusterPairs(pairs).sort((a, b) => b.uses - a.uses || byRecency(a, b))
  return best.seed
}

const BLOCKED_KEYS = new Set(SAVE_AS_VENUE_BLOCKLIST.map(venueNameKey))

/** A name that is not a place: under 3 characters, no key, or on the block list (D-05). */
export const isJunkVenueName = (name: string | null | undefined): boolean => {
  const trimmed = (name ?? '').trim()
  if (trimmed.length < SAVE_AS_VENUE_MIN_NAME_LENGTH) return true
  const key = venueNameKey(trimmed)
  return !key || BLOCKED_KEYS.has(key)
}

/**
 * Save as venue is offered at SAVE_AS_VENUE_MIN_EARLIER_USES earlier uses
 * (D-05). The history counts events and the candidate is still an
 * application, so useCount is "earlier uses": 2 means the candidate would be
 * the third.
 */
export const isSaveAsVenueEligible = (
  match: VenueHistoryMatch | null,
  venueName: string | null | undefined
): boolean =>
  match !== null && match.useCount >= SAVE_AS_VENUE_MIN_EARLIER_USES && !isJunkVenueName(venueName)

/** The custom venue's pin by the D-03 order: google_maps, then history, then none. */
export const pickDeskLocation = (
  googleMaps: string | null | undefined,
  match: VenueHistoryMatch | null
): DeskLocationPick => {
  const fromGoogle = parseGoogleMapsCoords(googleMaps)
  if (fromGoogle) return { location: fromGoogle, source: 'google_maps', places: 0 }
  const pair = match ? pickHistoryPair(match.pairs) : null
  if (match && pair) {
    return { location: `${pair.lat},${pair.lng}`, source: 'history', places: countHistoryPlaces(match.pairs) }
  }
  return { location: null, source: null, places: 0 }
}

/** The Save-as-venue prefill from a history match; empty strings for nulls (D-06). */
export const toVenueSuggestion = (match: VenueHistoryMatch, location: string | null): VenueSuggestion => ({
  name: match.venueName ?? '',
  address: match.address ?? '',
  description: match.description ?? '',
  descriptionPt: match.descriptionPt ?? '',
  phone: match.phone ?? '',
  website: match.website ?? '',
  location: location ?? ''
})
